/**
 * Parse common UI time strings (12h / 24h) to zero-padded `HH:mm`.
 */

export function parseWallTimeToHms(
  timeStr: string | null | undefined,
): { h: number; min: number; s: number } | null {
  const raw = timeStr?.trim() ?? ""
  if (!raw) return null

  const m12 = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)$/i.exec(raw)
  if (m12) {
    let h = Number(m12[1])
    const min = Number(m12[2])
    const s = m12[3] ? Number(m12[3]) : 0
    const ap = m12[4].toLowerCase()
    if (!Number.isFinite(h) || !Number.isFinite(min) || !Number.isFinite(s)) {
      return null
    }
    if (min < 0 || min > 59 || s < 0 || s > 59 || h < 1 || h > 12) return null
    if (ap === "pm" && h !== 12) h += 12
    if (ap === "am" && h === 12) h = 0
    return { h, min, s }
  }

  const m24 = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(raw)
  if (!m24) return null
  const h = Number(m24[1])
  const min = Number(m24[2])
  const s = m24[3] ? Number(m24[3]) : 0
  if (!Number.isFinite(h) || !Number.isFinite(min) || !Number.isFinite(s)) {
    return null
  }
  if (h < 0 || h > 23 || min < 0 || min > 59 || s < 0 || s > 59) return null
  return { h, min, s }
}

export function wallTimeToHHmm(
  timeStr: string | null | undefined,
): string | null {
  const hms = parseWallTimeToHms(timeStr)
  if (!hms) return null
  return `${String(hms.h).padStart(2, "0")}:${String(hms.min).padStart(2, "0")}`
}

/** `HH:mm` (24h) → `h:mm AM/PM` for time-picker parity (no timezone; clock-face only). */
export function hhmmTo12hLabelEnUs(
  hhmm: string | null | undefined,
): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm?.trim() ?? "")
  if (!m) return null
  const h24 = Number(m[1])
  const min = Number(m[2])
  if (
    !Number.isFinite(h24) ||
    !Number.isFinite(min) ||
    h24 < 0 ||
    h24 > 23 ||
    min < 0 ||
    min > 59
  ) {
    return null
  }
  const period = h24 >= 12 ? "PM" : "AM"
  let h12 = h24 % 12
  if (h12 === 0) h12 = 12
  return `${h12}:${String(min).padStart(2, "0")} ${period}`
}
